import { useEffect } from 'react';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { Spinner } from '@zapier/design-system';
import {
  resolveSignUpNextUrl,
  isNLAPluginSignUp,
  flushWithin,
  TRACKING_FLUSH_TIMEOUT_MS,
} from '@zapier/identity-utils';

import { PageLayout } from '../../../components';
import { dispatchSignupUserInteractionEvent } from '@zapier/authentication';

const rootStyle = css`
  padding: 40px;
`;

/**
 * This route handles returns from SSO authentications
 * the main goal is to redirect the user to the next url
 */
export default function SSOSuccess() {
  const router = useRouter();
  const { trackSignupConversion } = useTrackingContext();
  const isNLARedirect = isNLAPluginSignUp(router.query.utm_campaign);

  useEffect(() => {
    if (router.isReady) {
      const params = router.query as {
        next?: string;
        use_case?: string;
        use_case_slug?: string;
        selected_apis?: string;
        invitation_code?: string;
        from?: string;
        signup_type?: string;
        skipOnboarding?: string;
      };

      const next = params.next;
      const useCaseSlug = params.use_case || params.use_case_slug;
      const selected_apis = params.selected_apis;
      const invitation_code = params.invitation_code;
      const from = params.from;
      const signup_type = params.signup_type;
      const skipOnboarding = params.skipOnboarding || isNLARedirect;

      // Track that we completed an sso authentication.
      //
      // This page's only job is to redirect onward, typically to another app
      // (a full document unload). Firing the conversion fire-and-forget and
      // redirecting in the same tick let the navigation cut the request off
      // before it reached the backend / dataLayer, which is why organic Google
      // SSO step-2 completions were being dropped (WEBPX-5649). Wait for the
      // event to flush before redirecting, time-boxed so a slow request never
      // strands the user.
      const conversionTracked = trackSignupConversion('sso' as any, {
        useCaseSlug,
      });
      const redirect = () => {
        router.push(
          resolveSignUpNextUrl(
            next,
            {
              selected_apis,
              invitation_code,
              from,
            },
            { skipOnboarding }
          )
        );
      };
      if (signup_type === 'saml') {
        dispatchSignupUserInteractionEvent({
          signupFunnel: 'signup',
          signupType: 'sso',
          selectedApis: selected_apis,
        })?.then(() => {
          redirect();
        });
      } else {
        void flushWithin(conversionTracked, TRACKING_FLUSH_TIMEOUT_MS).then(
          redirect
        );
      }
    }
    // next router does not have router.query populated on first render
  }, [router.isReady]);

  return (
    <PageLayout>
      <div css={rootStyle}>
        <Spinner size="medium" />
      </div>
    </PageLayout>
  );
}
